import { useState, useEffect, useRef, useCallback } from "react";
import { getJSONCookie, setJSONCookie, deleteCookie } from "../utils/cookies";
import { calculatePayslip } from "./Calculations";

const COOKIE_NAME = "payrollcy_payslip_draft";

const DEFAULT_STATE = {
  employer: { name: "", taxId: "", employmentType: "", currency: "EUR" },
  employee: { name: "", employeeId: "", siNumber: "", position: "", paymentMethod: "" },
  payPeriod: { start: "", end: "", paymentDate: "", paidDays: "", lossOfPayDays: "" },
  earnings: [
    { id: "basic", label: "Basic salary", amount: "" },
    { id: "overtime", label: "Overtime pay", amount: "" },
    { id: "thirteenth", label: "13th / 14th salary", amount: "" },
    { id: "bonus", label: "Bonus", amount: "" },
    { id: "transport", label: "Transport allowance", amount: "" },
  ],
  otherDeductions: [
    { id: "loan", label: "Loan repayments", amount: "" },
    { id: "union", label: "Union fees", amount: "" },
  ],
  notes: "",
};

export function usePayslipForm() {
  const [formData, setFormData] = useState(() => getJSONCookie(COOKIE_NAME) || DEFAULT_STATE);
  const debounceRef = useRef(null);

  // Persist to a cookie whenever formData changes — debounced so typing
  // a name doesn't write a cookie on every single keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setJSONCookie(COOKIE_NAME, formData);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [formData]);

  const updateField = useCallback((section, field, value) => {
    setFormData((prev) => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  }, []);

  const updateLine = useCallback((section, id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: prev[section].map((line) => (line.id === id ? { ...line, [field]: value } : line)),
    }));
  }, []);

  const addLine = useCallback((section, defaultLabel = "New item") => {
    setFormData((prev) => ({
      ...prev,
      [section]: [...prev[section], { id: `${section}-${Date.now()}`, label: defaultLabel, amount: "" }],
    }));
  }, []);

  const removeLine = useCallback((section, id) => {
    setFormData((prev) => ({
      ...prev,
      [section]: prev[section].filter((line) => line.id !== id),
    }));
  }, []);

  const updateNotes = useCallback((value) => {
    setFormData((prev) => ({ ...prev, notes: value }));
  }, []);

  const resetForm = useCallback(() => {
    deleteCookie(COOKIE_NAME);
    setFormData(DEFAULT_STATE);
  }, []);

  const grossEarnings = formData.earnings.reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0);
  const otherDeductionsTotal = formData.otherDeductions.reduce(
    (sum, l) => sum + (parseFloat(l.amount) || 0),
    0
  );

  const calculation = calculatePayslip({
    grossEarnings,
    otherDeductions: otherDeductionsTotal,
    periodsPerYear: 12,
  });

  return {
    formData,
    updateField,
    updateLine,
    addLine,
    removeLine,
    updateNotes,
    resetForm,
    grossEarnings,
    otherDeductionsTotal,
    calculation,
  };
}